import {
  ConversationItems,
  PageReader,
  PageTransitionFrame,
  type ReaderAdvance,
  type ReaderFormat,
  type ReaderNoyau,
  type ReaderValue,
  asStyle,
  readerMotion,
  readerThemeStyle,
  usePrefersReducedMotion,
} from '@/components/reader';
import type { ThreadSlot } from '@/lib/session-conversation';
import type {
  DisplayConditionSources,
  ExperiencePage,
  ExperienceVariable,
  SessionAdvance,
  SessionValues,
  SessionView,
} from '@matter_extractor/shared';
import {
  conversationItemsOf,
  isBlockDisplayed,
  isConversationPage,
  pageOf,
  playablePages,
  resolvePersonVariable,
  resolveTheme,
} from '@matter_extractor/shared';
import { type ReactNode, useState } from 'react';
import { SESSION_TEST_IDS } from './contracts';
import {
  feedbackOffered,
  feedbackThanksOfView,
  onFeedbackPage,
  openFeedbackAdvance,
} from './feedback';
import { type ConversationChannel, ProtocolConversation } from './protocol-conversation';
import { SessionEnding } from './session-ending';

/**
 * « Regarder » (npc.md §11, issue #210): the operator watches a replay as the person sees it. The
 * screen is the SAME player on the same view, drawn READ-ONLY: nothing the page offers answers, no
 * advance leaves, the field of the conversation is drawn and inert. `note` is the sentence the
 * inert field carries (« Le joueur répondra quand Vivianne aura fini »); `revealed` - how many
 * temps of the page are on screen - is the playback's to drive when it replays a recorded sequence,
 * and absent when the live view lets the page play its own temps.
 */
export interface SessionWatching {
  readonly note: string;
  readonly revealed?: number;
}

export interface SessionPlayerProps {
  readonly view: SessionView;
  readonly format: ReaderFormat;
  readonly onAdvance: (advance: SessionAdvance) => void;
  /**
   * An advance is in flight, or waits on the determination of the noyau
   * (noyau.md §4.6, N87; issue #174): for all that time the button pressed
   * says so - the protocol page's « Continuer » its « Un instant... », a Bouton
   * its own waiting label - and the page answers nothing else.
   */
  readonly advancing: boolean;
  /** A refusal of the last advance, worded from its code. */
  readonly refusal: string | null;
  /** The live half of the protocol page (§9.5) - owned by the route, which holds the channel. */
  readonly conversation: ConversationChannel;
  /** Given, the player is a READ-ONLY window on somebody else's session (see `SessionWatching`). */
  readonly watching?: SessionWatching;
}

/**
 * The session, played (§5.3, §9.2 to §9.5) - and « toute la fenêtre est peinte
 * par le thème, rien de PMS n'y subsiste ».
 *
 * ## What this component owns
 *
 * The VALUES of the page being played, and nothing else. The view comes from
 * the server on every read, the advance is the route's mutation, and the theme
 * is the reader's own business. The values are React state seeded from the
 * view and never repaired by an effect: the page is mounted with a `key`
 * carrying the page id, so « la page a changé » is a REMOUNT - the repository's
 * idiom for reloading a state, and what §5.3 asks for besides (« une page
 * reprise rejoue ses temps d'apparition depuis le début », which is exactly
 * what a remount of the reader does).
 *
 * ## Three things can stand in the window
 *
 * A content page - the reader, plain. The protocol page - the reader painting
 * the window, with the CONVERSATION of §9.5 over it, because a protocol page
 * has no block and the reader would otherwise draw an empty column. And an END:
 * the sober screen of an expired session, or the engine's excuse on a closed
 * one. `finished` is NOT one of them - « la dernière page EST la fin, le
 * dispositif n'ajoute aucun écran de remerciement derrière elle » - so the last
 * page stays on screen, with its advance refused.
 *
 * ## The page of retour (retour-experience.md, issue #176)
 *
 * It bends three of those rules and no more. A CLOSED session whose experience
 * has a page of retour keeps its excuse and gains « Donner mon avis », which
 * posts the one advance the server admits (§6.2). Once the person is ON that
 * page, the closing panel gives way to it - a closed or a finished session
 * answers its « Envoyer », the only write either admits (§5) - and once the
 * feedback is sent the page's blocks give way to the phrase of thanks. An
 * expired session shows the sober screen whatever its experience holds (§6.3).
 *
 * The seam of #60 stood in that very place on `protocolMode === 'seam'`, and it
 * was always described as one line that would move. It moved here: #64 made
 * `sessionProtocolMode` answer `conversation`, and between the two lots a
 * protocol page rendered NOTHING at all.
 */
export function SessionPlayer({
  view,
  format,
  onAdvance,
  advancing,
  refusal,
  conversation,
  watching,
}: SessionPlayerProps) {
  const page = pageOf(view.content.pages, view.currentPageId);
  const prefersReducedMotion = usePrefersReducedMotion();
  const visual = resolveTheme(view.content.theme, page?.visual ?? {});
  const motion = readerMotion(visual, prefersReducedMotion);
  const feedbackPage = onFeedbackPage(page);
  const feedback = feedbackOffered(view, page);
  /*
    What stands over the page, if anything (§5.3, retour-experience.md §6): an
    expired session ALWAYS - « la page de retour n'est pas jouée » (H2) - and an
    interrupted one just the same (issue #199: no page de fin, no page de retour),
    and a closed one except on the page of retour, where the person was taken by
    « Donner mon avis » and the page, or its thanks, is the screen. Without that
    exception the closing panel would cover the very page it offers.
  */
  const ending: 'expired' | 'closed' | 'interrupted' | null =
    view.status === 'expired'
      ? 'expired'
      : view.status === 'interrupted'
        ? 'interrupted'
        : view.status === 'closed' && !feedbackPage
          ? 'closed'
          : null;
  const over = ending !== null;

  /*
    The transition of §9.3 is carried by the edge LEAVING a page, so the one to
    play on arrival is the PREVIOUS playable page's. The session only ever
    advances - it has no back - which is why `PageTransitionFrame` is enough:
    it keeps the arriving page and nothing else. Walked under the state of the
    determination, so a skipped sequence is not the page « before » (§4.5).
  */
  /*
    THE THREAD (prolongation.md §5, issue #186): the conversation stays mounted
    from the protocol page to the last page played in it, so those pages are ONE
    screen - no transition between them, and the page under the conversation
    stays the protocol page's, drawn once. The transition that plays is the one
    into the thread (the edge leaving the page before the protocol page) and the
    one out of it (the edge leaving the LAST page of the thread, which is the
    previous playable page of the ordinary page that follows).
  */
  const protocolPage = view.content.pages.find((one) => one.kind === 'protocol') ?? null;
  const threadPage =
    view.protocolMode === 'conversation' && page !== null && isConversationPage(page);
  const inThread =
    view.protocolMode === 'conversation' &&
    page !== null &&
    (page.kind === 'protocol' || threadPage) &&
    protocolPage !== null;
  const arriving = inThread ? protocolPage : page;
  const playable = playablePages(view.content.pages, view.noyau?.determination ?? null);
  const index = arriving === null ? -1 : playable.findIndex((one) => one.id === arriving.id);
  const previous = index > 0 ? playable[index - 1] : undefined;

  return (
    <div
      data-testid={SESSION_TEST_IDS.player}
      /* The frame the theme paints: the whole window, a definite height all the
         way down to the box that scrolls the page (see below). The child
         selector is what gives the transition frame that height without
         reaching into a component another lot owns. */
      className={`${watching === undefined ? 'h-dvh' : 'h-full'} w-full overflow-hidden [&>*]:h-full`}
      style={asStyle(readerThemeStyle(visual, format))}
    >
      {page === null ? (
        <SessionWindow>
          <SessionEnding kind="expired" reason={null} />
        </SessionWindow>
      ) : (
        <PageTransitionFrame
          transition={previous?.transition ?? { animation: 'none', speed: 'normal' }}
          motion={motion}
          pageKey={arriving?.id ?? page.id}
        >
          <div className="relative h-full">
            {/*
              The page SCROLLS here, and not in the reader (issue #168).
              `.lecteur` is `overflow-y: auto` on a `min-height: 100%`, and a
              minimum bounds nothing: the reader grew with its content, the
              frame's `overflow-hidden` clipped it, and a page of choice taller
              than the window hid « Choisir ce texte » where no wheel reaches.
              This box is the frame's height, so the reader fills it at least
              and scrolls in it beyond - the régime of the page editor's
              preview, whose frame scrolls the same reader. The layers below
              stay its SIBLINGS: inside it, an end or a refusal would scroll
              away with the page.
            */}
            <div
              data-testid={SESSION_TEST_IDS.scroll}
              className="h-full overflow-y-auto overflow-x-hidden"
            >
              <PlayedPage
                key={arriving?.id ?? page.id}
                /* Under the conversation the page is drawn WITHOUT its elements:
                   they are in the thread, and the window is painted once. */
                page={threadPage ? { ...page, temps: [], autoDurationSeconds: null } : page}
                view={view}
                format={format}
                onAdvance={onAdvance}
                /* The page of retour is the one page a finished or a closed
                   session still ANSWERS: its « Envoyer » is admitted on both
                   (retour-experience.md §5), where every other last page keeps
                   its advance refused. */
                inert={
                  over ||
                  advancing ||
                  (view.status === 'finished' && !feedbackPage) ||
                  watching !== undefined
                }
                advancing={advancing}
                thanks={feedbackThanksOfView(view, page)}
                {...(watching === undefined ? {} : { watching })}
              />
            </div>
            {ending !== null && (
              <SessionWindow>
                <SessionEnding
                  kind={ending}
                  reason={view.closeReason}
                  {...(feedback
                    ? {
                        feedback: {
                          onGive: () => {
                            if (!advancing) onAdvance(openFeedbackAdvance(page.id));
                          },
                          waiting: advancing,
                          buttonStyle: visual.buttonStyle,
                        },
                      }
                    : {})}
                />
              </SessionWindow>
            )}
            {!over && inThread && (
              <SessionSurface>
                <ThreadConversation
                  view={view}
                  page={page}
                  format={format}
                  channel={conversation}
                  advancing={advancing}
                  reducedMotion={prefersReducedMotion}
                  /* A session the skip of the noyau ENDED on this page (noyau.md
                     §4.5, N27) offers no « Continuer »: its advance could only
                     be refused. The last page of the experience, played in the
                     thread, ends the same way, and its goodbye comes after the
                     click, with no « Continuer » (prolongation.md §9). */
                  finished={view.status === 'finished'}
                  onAdvance={onAdvance}
                  {...(watching === undefined ? {} : { watching })}
                />
              </SessionSurface>
            )}
            {refusal !== null && (
              <p
                role="alert"
                className="absolute inset-x-0 bottom-0 z-20 m-0 p-4 text-center"
                style={{
                  color: 'var(--lecteur-color-muted)',
                  fontSize: 'var(--lecteur-small-size)',
                }}
              >
                {refusal}
              </p>
            )}
          </div>
        </PageTransitionFrame>
      )}
    </div>
  );
}

/**
 * What stands OVER the reader, CENTRED: the two ends of §5.3.
 *
 * Over and not instead of, so the window keeps the background, the veil and the
 * face the theme paints - the reader is the only thing that knows how to draw
 * those, and a second painter would be a second §9.4. The layer lets the
 * pointer through and its child takes it back, so a panel in the middle of the
 * window does not make the whole page unclickable.
 */
function SessionWindow({ children }: { readonly children: React.ReactNode }) {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center p-6">
      <div className="pointer-events-auto">{children}</div>
    </div>
  );
}

/**
 * What stands over the reader and FILLS it: the conversation of §9.5.
 *
 * The same layer as `SessionWindow` and the same reason for existing, with two
 * differences that the conversation cannot do without. It takes the whole
 * height, because the thread scrolls between a header and a composer and a
 * centred box has no height to scroll inside; and it takes the pointer
 * outright, because every part of it is either typed into or pressed.
 */
function SessionSurface({ children }: { readonly children: React.ReactNode }) {
  return <div className="absolute inset-0 z-10 flex flex-col">{children}</div>;
}

interface PlayedPageProps {
  readonly page: ExperiencePage;
  readonly view: SessionView;
  readonly format: ReaderFormat;
  readonly onAdvance: (advance: SessionAdvance) => void;
  /** The page is on screen but no longer answers - an end, or an advance in flight. */
  readonly inert: boolean;
  /** The advance is in flight or waits on the noyau: the Bouton pressed says so (#174). */
  readonly advancing: boolean;
  /**
   * The phrase of thanks of the page of retour once its feedback is sent, or
   * null (retour-experience.md §5): it replaces the page's blocks.
   */
  readonly thanks: string | null;
  /** The player is a read-only window (`SessionWatching`): nothing typed or ticked is kept. */
  readonly watching?: SessionWatching;
}

/**
 * One page, with the values the person has typed into it.
 *
 * Mounted with a `key` on the page id, so every state below is seeded once per
 * page and the temps of §9.3 play again on a reprise. The values are seeded
 * from the SESSION and not from nothing: « la session enregistre la page
 * courante et les valeurs déjà saisies » (§5.3), and a reprise that came back
 * to an empty form would be a promise broken in the one place it is noticed.
 */
function PlayedPage({
  page,
  view,
  format,
  onAdvance,
  inert,
  advancing,
  thanks,
  watching,
}: PlayedPageProps) {
  const [values, setValues] = useState<SessionValues>(() => view.values);
  /* The Bouton that sent the last advance: the one that says its waiting label
     for as long as that advance is in flight or waits on the noyau (noyau.md
     §4.6, N87; issue #174) - and only a BOUTON: « Choisir ce texte » keeps its
     fixed label (N29) and an automatic duration presses nothing. Reset with
     the page, by its key. */
  const [pressed, setPressed] = useState<string | null>(null);

  const advance = (reader: ReaderAdvance): void => {
    if (inert) return;
    setPressed(reader.reason === 'button' ? reader.block.id : null);
    if (reader.reason === 'noyau') {
      // « Choisir ce texte » (noyau.md §5.1): the Noyau block is what was
      // pressed, and the card confirmed travels by its OPAQUE identifier - the
      // server alone knows which noyau it is (N28).
      onAdvance({
        pageId: page.id,
        values,
        pressedBlockId: reader.block.id,
        cardId: reader.cardId,
      });
      return;
    }
    const pressedBlockId = reader.reason === 'button' ? reader.block.id : null;
    onAdvance({ pageId: page.id, values, pressedBlockId });
  };

  const noyau = readerNoyauOf(view);

  return (
    <PageReader
      page={page}
      theme={view.content.theme}
      format={format}
      mode="interactive"
      values={values}
      onValueChange={(blockId: string, value: ReaderValue) => {
        // A window on somebody else's session keeps nothing the operator types.
        if (watching === undefined) setValues((current) => ({ ...current, [blockId]: value }));
      }}
      onAdvance={advance}
      {...(watching?.revealed === undefined
        ? {}
        : { revealed: watching.revealed, onRevealed: () => undefined })}
      /* The display conditions of §9.2 (H70, H72), evaluated on what the SERVER
         evaluates at the advance: the values the session holds - never what is
         typed on this page, which a condition cannot cite (H60) - the retained
         noyau and the statuses of the objectives. One function on one set of
         values, so a masked required field greys nothing here and refuses
         nothing there. */
      conditions={sessionConditions(view)}
      {...(noyau === undefined ? {} : { noyau })}
      /* From the moment its advance leaves - the request, then the wait on the
         noyau - until the session moves or refuses (F4): a refusal ends
         `advancing`, and the Bouton says its label again. */
      waitingBlockId={advancing ? pressed : null}
      thanks={thanks}
      /* « Les variables se résolvent depuis les valeurs de la session » (§8.6):
         the ones typed on THIS page, over the ones the session already holds -
         a `{{champ:...}}` of a previous page is not in this page's blocks. An
         objectif renders the person's own extracts, one per line, and a critère
         its frozen direction, both off the view (H63); and the resolver is TOTAL
         (D4, H62): nothing the person reads ever carries a raw token, an empty
         answer being a fact of the session and not a defect of the experience. */
      resolveVariable={(variable) => resolveSessionVariable(view, values, variable)}
    />
  );
}

/*
  What the Noyau blocks of a page show (noyau.md §5): the two cards the view
  dealt for the page's choice, and the portrait of the retained noyau - served
  by the server off the texts it froze, never looked up here.
*/
function readerNoyauOf(view: SessionView): ReaderNoyau | undefined {
  const served = view.noyau;
  return served === undefined
    ? undefined
    : {
        choices: served.choice === null ? {} : { [served.choice.blockId]: served.choice.cards },
        portrait: served.portrait,
      };
}

/**
 * The display conditions of §9.2 (H70, H72), evaluated on what the SERVER
 * evaluates at the advance: the values the session holds - never what is typed
 * on this page, which a condition cannot cite (H60) - the retained noyau and the
 * statuses of the objectives. One function on one set of values, so a masked
 * required field greys nothing here and refuses nothing there.
 */
function sessionConditions(view: SessionView): DisplayConditionSources {
  return {
    values: view.values,
    // `view.noyau.retained` IS the server's `displaySourcesOf(...).noyau`, the
    // value the advance evaluates on - one reading, never a second one written
    // beside it (H72). Null until the first choice is confirmed.
    noyau: view.noyau?.retained ?? null,
    objectives: view.objectives ?? {},
    pages: view.content.pages,
  };
}

/**
 * « Les variables se résolvent depuis les valeurs de la session » (§8.6): the
 * ones typed on THIS page, over the ones the session already holds - a
 * `{{champ:...}}` of a previous page is not in this page's blocks. An objectif
 * renders the person's own extracts, one per line, and a critère its frozen
 * direction, both off the view (H63); and the resolver is TOTAL (D4, H62):
 * nothing the person reads ever carries a raw token, an empty answer being a fact
 * of the session and not a defect of the experience.
 */
function resolveSessionVariable(
  view: SessionView,
  values: SessionValues,
  variable: ExperienceVariable,
): string | null {
  return resolvePersonVariable(
    {
      values: { ...view.values, ...values },
      matter: view.matter,
      criterionDirections: view.criterionDirections,
      // `@noyau` renders the deposited name of the retained noyau (N11).
      noyau: view.noyau?.retained ?? null,
    },
    variable,
  );
}

interface ThreadConversationProps {
  readonly view: SessionView;
  /** The page the session stands on: the protocol page, or a page of the thread. */
  readonly page: ExperiencePage;
  readonly format: ReaderFormat;
  readonly channel: ConversationChannel;
  readonly advancing: boolean;
  readonly reducedMotion: boolean;
  readonly finished: boolean;
  readonly onAdvance: (advance: SessionAdvance) => void;
  readonly watching?: SessionWatching;
}

/**
 * The conversation, and what it hangs under the bubbles of the pages played in
 * it (prolongation.md §5).
 *
 * It owns what `PlayedPage` owns for a page - the VALUES the person gives and the
 * Bouton pressed - because the elements are the page's own and leave by the same
 * advance: `values`, the button or the card, the same `POST advance`, the same
 * refusals. The values are seeded once from the view and kept across the pages
 * of the thread (a block id belongs to one page), so a Case ticked here travels
 * with the advance and is read back from the view afterwards.
 *
 * What is NOT kept here is the click: once the advance is accepted the view
 * carries it (`kind: 'click'`) and the thread draws THAT as the person's
 * bubble - no optimistic bubble to reconcile.
 */
function ThreadConversation({
  view,
  page,
  format,
  channel,
  advancing,
  reducedMotion,
  finished,
  onAdvance,
  watching,
}: ThreadConversationProps) {
  const [values, setValues] = useState<SessionValues>(() => view.values);
  const [pressed, setPressed] = useState<string | null>(null);

  const advance = (reader: ReaderAdvance): void => {
    if (advancing || finished || watching !== undefined) return;
    setPressed(reader.reason === 'button' ? reader.block.id : null);
    if (reader.reason === 'noyau') {
      onAdvance({
        pageId: page.id,
        values,
        pressedBlockId: reader.block.id,
        cardId: reader.cardId,
      });
      return;
    }
    onAdvance({
      pageId: page.id,
      values,
      pressedBlockId: reader.reason === 'button' ? reader.block.id : null,
    });
  };

  const renderItems = (slot: ThreadSlot): ReactNode => {
    const slotPage = pageOf(view.content.pages, slot.pageId);
    if (slotPage === null) return null;
    return (
      <ConversationItems
        page={slotPage}
        theme={view.content.theme}
        format={format}
        mode={slot.mode}
        waitingForCards={slot.mode === 'live' && awaitsCards(view, slotPage)}
        values={values}
        onValueChange={(blockId, value) => {
          if (watching === undefined) setValues((current) => ({ ...current, [blockId]: value }));
        }}
        onAdvance={advance}
        resolveVariable={(variable) => resolveSessionVariable(view, values, variable)}
        conditions={sessionConditions(view)}
        noyau={readerNoyauOf(view)}
        waitingBlockId={advancing ? pressed : null}
        reducedMotion={reducedMotion}
      />
    );
  };

  return (
    <ProtocolConversation
      persona={view.persona}
      channel={{ ...channel, renderItems }}
      advancing={advancing}
      reducedMotion={reducedMotion}
      finished={finished}
      {...(watching === undefined ? {} : { watching: watching.note })}
      onContinue={() => {
        if (watching === undefined)
          onAdvance({ pageId: page.id, values: {}, pressedBlockId: null });
      }}
    />
  );
}

/**
 * The page's cards are not dealt yet: it shows a Noyau block of choice and the
 * view holds no cards for it - the determination is running, or has just been
 * rendered and the view not re-read (prolongation.md §4.5). The elements of the
 * page then stand under two ghost cards.
 */
function awaitsCards(view: SessionView, page: ExperiencePage): boolean {
  const choice = conversationItemsOf(page).find(
    (block) => block.type === 'noyau' && block.mode !== 'portrait',
  );
  if (choice === undefined || !isBlockDisplayed(choice, sessionConditions(view))) return false;
  return view.noyau?.choice?.blockId !== choice.id;
}
